import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useScreenSize } from '../../../../core/hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../../core/hooks/useWindowBreakpoints';
import CourseActions from '../courseActions';
import CourseTags from '../courseTags';

interface CourseDetailsProps {
  launchedAt?: Date;
  tags: string[];
  title: string;
  slug: string;
  teacher?: string;
  courseType: 'regular' | 'mini' | 'regional';
  courseStatus: 'notstarted' | 'inprogress' | 'finished';
  tagsPosition?: 'aboveTitle' | 'bellowTitle';
  className?: string;
  pdfSummaryUri?: string;
  moveHeaderOnMobile?: boolean;
  openCourseProgramModal?: () => void;
  showDropButton?: boolean;
  isLanding?: boolean;
}

const CourseInfo = ({
  launchedAt,
  tags,
  title,
  slug,
  teacher,
  courseType,
  courseStatus,
  tagsPosition = 'aboveTitle',
  className,
  openCourseProgramModal,
  moveHeaderOnMobile = false,
  showDropButton,
  isLanding = false,
  pdfSummaryUri
}: CourseDetailsProps) => {
  const screenSize = useScreenSize();
  const windowBreakpoints = useWindowBreakpoints();
  const renderCourseTags = useMemo(() => <CourseTags tags={tags} />, [tags]);

  const renderActions = useCallback(
    () => (
      <CourseActions
        slug={slug}
        pdfSummaryUri={pdfSummaryUri}
        showDropButton={showDropButton}
        courseType={courseType}
        courseStatus={courseStatus}
        moveHeaderOnMobile={moveHeaderOnMobile}
        openCourseProgramModal={openCourseProgramModal}
        isLanding={isLanding}
        courseName={title}
      />
    ),
    [courseType, courseStatus, openCourseProgramModal]
  );

  return (
    <div className={className}>
      <div className={`flex flex-col gap-xl`}>
        <div className='flex flex-col gap-xs sm:gap-sm'>
          {courseType === 'regular' && courseStatus !== 'notstarted' && (
            <Typography
              size={screenSize >= windowBreakpoints.md ? 'h6' : 'h5'}
              weight='bold'
              type='titleSection'
              className={`md:text-card-color-default md:text-h5 ${
                screenSize >= windowBreakpoints.md || !moveHeaderOnMobile
                  ? '!text-card-color-default'
                  : ''
              }`}
            >
              SEU CURSO REGULAR
            </Typography>
          )}
          {launchedAt && (
            <Typography
              type='titleSection'
              size='h5'
              className={`md:text-white ${
                screenSize >= windowBreakpoints.md || !moveHeaderOnMobile
                  ? '!text-white'
                  : '!text-mono-700'
              }`}
            >
              Lançado em {format(new Date(launchedAt), 'dd/MM/yyyy')}
            </Typography>
          )}
          {tagsPosition === 'aboveTitle' && renderCourseTags}
          <Typography
            type='title'
            size='h1'
            className={`md:text-card-color-default sm:!text-[50.82px]`}
          >
            {title}
          </Typography>
          {tagsPosition === 'bellowTitle' && renderCourseTags}
          {teacher && (
            <Typography
              className={`hidden sm:block md:text-white ${
                screenSize >= windowBreakpoints.md || !moveHeaderOnMobile
                  ? '!text-white'
                  : '!text-mono-700'
              }}`}
            >
              Prof. {teacher}
            </Typography>
          )}
        </div>
        {renderActions()}
      </div>
    </div>
  );
};

export default CourseInfo;
